import React, { useEffect } from "react";
import { usePageSubHeader } from "../../contexts/PageSubHeaderContext";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";
import Container from "./Container";
import Skeleton from "../ui/Skeleton";

interface PageLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: React.ReactNode;
  header?: React.ReactNode;
  actions?: React.ReactNode;
  containerSize?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  showContainer?: boolean;
  titleVariant?: "default" | "gradient" | "bold";
  padding?: "none" | "sm" | "md" | "lg";
  loading?: boolean;
  skeleton?: React.ReactNode;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  className,
  title: _title,
  subtitle,
  header: _header,
  actions,
  containerSize = "xl",
  showContainer = true,
  titleVariant: _titleVariant,
  padding = "md",
  loading = false,
  skeleton,
  children,
  ...props
}) => {
  const { setPageMeta } = usePageSubHeader();
  const { t } = useTranslation();

  // Envia subtitle/actions para o DashboardLayout renderizar na strip do layout
  useEffect(() => {
    if (loading) {
      setPageMeta(
        <Skeleton width={300} height={12} className="opacity-40" />,
        <div className="flex gap-2">
          <Skeleton width={80} height={32} className="rounded-lg opacity-30" />
          <Skeleton
            width={32}
            height={32}
            className="rounded-full opacity-20"
          />
        </div>,
      );
    } else {
      const translatedSubtitle =
        typeof subtitle === "string" ? t(subtitle) : subtitle;
      setPageMeta(translatedSubtitle ?? null, actions ?? null);
    }
    return () => setPageMeta(null, null);
  }, [subtitle, actions, loading, setPageMeta, t]);

  const content = (
    <div
      className={cn(
        "py-0 space-y-3 sm:space-y-3.5 md:space-y-4 lg:space-y-5",
        className,
      )}
    >
      {loading && skeleton ? skeleton : children}
    </div>
  );

  if (showContainer) {
    return (
      <Container size={containerSize} padding={padding} {...props}>
        {content}
      </Container>
    );
  }

  return (
    <Container
      size="full"
      padding={padding}
      fluid
      className={cn("layout-flush", className)}
      {...props}
    >
      {content}
    </Container>
  );
};

export default PageLayout;
